import React from "react";
import _ from "lodash";
import { useDispatch } from "react-redux";

import useAssetSelectionSearch
  from "../../../../../../../RiskProfiling/components/StepContent/components/step/ProductSelectionStepsV2/hooks/useFilters";
import {
  displaySuccessSnackBar,
  displayWarningSnackBar,
} from "../../../../../../../../components/SnackbarProvider/actions";
import {
  MUSTERDEPOT_ALL_ASSETS_AVAILABLE,
  MUSTERDEPOT_NOT_ALL_ASSETS_AVAILABLE,
} from "../../../../../../../RiskProfiling/components/StepContent/components/step/ProductsSelectionSteps/components/InvestmentRecommendationStep/components/MusterDepot/constants";

import {STEPS, SOURCES, ACTIONS} from "./constants";
import {reducer, createInitialState} from "./reducer";


/**
 * State management hook for instrument selection modals.
 * Handles: reducer, search, dispatch handlers, derived state.
 */
export function useInstrumentSelection({
  existingInstruments,
  portfolioInstruments,
  initialActiveAssets,
  custodianId,
  onSubmit = () => {},
  onClose,
}) {
  const dispatch = useDispatch();
  const [state, selectionDispatch] = React.useReducer(reducer, createInitialState(initialActiveAssets));
  const {step, activeSource, activeSourceItem, selectedAssets, activeAssets} = state;

  const [assetsSearch, handleSearchActivate, addExtraFilters] = useAssetSelectionSearch(
    {custodian: custodianId}, dispatch
  );

  const disabledInstruments = React.useMemo(
    () => _.concat(selectedAssets, existingInstruments, portfolioInstruments || []),
    [selectedAssets, existingInstruments, portfolioInstruments]
  );

  const hasSelectedAssets = selectedAssets.some(a => a.selected);
  const nextDisabled = step === STEPS.MAIN
    ? !hasSelectedAssets
    : _.isEmpty(activeAssets);

  const handleAddAssetFromSearch = React.useCallback((asset) => {
    selectionDispatch({type: ACTIONS.CONFIRM_ASSETS_SELECTION, payload: {assets: [asset]}});
  }, []);

  const handleToggleAsset = React.useCallback((asset) => {
    selectionDispatch({type: ACTIONS.TOGGLE_SELECTED_ASSET, payload: asset});
  }, []);

  const onSelectAllAssets = React.useCallback((selectAll) => {
    selectionDispatch({type: ACTIONS.SELECT_ALL_ASSETS, payload: selectAll});
  }, []);

  const onInstrumentSelect = React.useCallback((asset) => {
    selectionDispatch({type: ACTIONS.TOGGLE_ASSET, payload: asset});
  }, []);

  const onSelectAll = React.useCallback((assets) => {
    selectionDispatch({type: ACTIONS.SET_ACTIVE_ASSETS, payload: assets});
  }, []);

  const onFiltersClick = React.useCallback(() => {
    selectionDispatch({type: ACTIONS.SET_STEP, payload: STEPS.FILTERS});
  }, []);

  const onFiltersAddInstruments = React.useCallback((instruments) => {
    selectionDispatch({type: ACTIONS.CONFIRM_ASSETS_SELECTION, payload: {assets: instruments}});
  }, []);

  const selectSource = React.useCallback((source) => {
    selectionDispatch({
      type: ACTIONS.SELECT_SOURCE,
      payload: {source, step: source === SOURCES.PORTFOLIO ? STEPS.PORTFOLIO : undefined},
    });
  }, []);

  const handleDetailsClick = React.useCallback((item, skipDetails, hasWarning) => {
    if (skipDetails) {
      if (activeSource === SOURCES.MUSTER_DEPOT) {
        if(hasWarning){
          dispatch(displayWarningSnackBar(MUSTERDEPOT_NOT_ALL_ASSETS_AVAILABLE));
        } else {
          dispatch(displaySuccessSnackBar(MUSTERDEPOT_ALL_ASSETS_AVAILABLE));
        }
      }
      selectionDispatch({type: ACTIONS.CONFIRM_ASSETS_SELECTION, payload: {assets: item.assets}});
    } else {
      selectionDispatch({type: ACTIONS.SELECT_SOURCE_ITEM, payload: item});
    }
  }, [activeSource, dispatch]);

  const handleSourceNext = () => {
    selectionDispatch({type: ACTIONS.CONFIRM_ASSETS_SELECTION, payload: {assets: activeAssets}});
  };

  const handleSubmit = () => {
    onSubmit(selectedAssets.filter(a => a.selected));
    onClose();
  };

  const goBack = () => selectionDispatch({type: ACTIONS.GO_BACK});

  return {
    step,
    activeSource,
    activeSourceItem,
    selectedAssets,
    activeAssets,
    assetsSearch,
    handleSearchActivate,
    addExtraFilters,
    disabledInstruments,
    hasSelectedAssets,
    nextDisabled,
    handleAddAssetFromSearch,
    handleToggleAsset,
    onSelectAllAssets,
    onInstrumentSelect,
    onSelectAll,
    onFiltersClick,
    onFiltersAddInstruments,
    selectSource,
    handleDetailsClick,
    handleSourceNext,
    handleSubmit,
    goBack,
  };
}
